import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Label } from '../components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import AboutModal from '../components/AboutModal';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function BrowseTalks({ user }) {
  const navigate = useNavigate();
  const [talks, setTalks] = useState([]);
  const [filteredTalks, setFilteredTalks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ speaker: '', club: '', topic: '', minRating: 0 });
  const [sortBy, setSortBy] = useState('speaker');
  const [sortOrder, setSortOrder] = useState('asc');
  const [bookingDialog, setBookingDialog] = useState(false);
  const [ratingDialog, setRatingDialog] = useState(false);
  const [loginPromptDialog, setLoginPromptDialog] = useState(false);
  const [selectedTalk, setSelectedTalk] = useState(null);
  const [bookingInfo, setBookingInfo] = useState(null);
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState(null);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    fetchTalks();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [talks, filters, sortBy, sortOrder]);

  const fetchTalks = async () => {
    try {
      const response = await axios.get(`${API}/talks`);
      setTalks(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch talks:', err);
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let result = [...talks];

    // Apply filters
    if (filters.speaker) {
      result = result.filter(t => 
        t.speaker_name?.toLowerCase().includes(filters.speaker.toLowerCase())
      );
    }
    if (filters.club) {
      result = result.filter(t => 
        t.original_club?.toLowerCase().includes(filters.club.toLowerCase())
      );
    }
    if (filters.topic) {
      result = result.filter(t =>
        t.topic_name?.toLowerCase().includes(filters.topic.toLowerCase())
      );
    }
    if (filters.minRating > 0) {
      result = result.filter(t => t.average_rating && t.average_rating >= filters.minRating);
    }

    // Apply sort
    result.sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case 'speaker':
          aVal = a.speaker_name || '';
          bVal = b.speaker_name || '';
          break;
        case 'topic':
          aVal = a.topic_name || '';
          bVal = b.topic_name || '';
          break;
        case 'club':
          aVal = a.original_club || '';
          bVal = b.original_club || '';
          break;
        case 'date':
          aVal = a.original_date || '';
          bVal = b.original_date || '';
          break;
        case 'deliveries':
          aVal = a.delivery_count || 0;
          bVal = b.delivery_count || 0;
          break;
        case 'rating':
          aVal = a.average_rating ?? -1;
          bVal = b.average_rating ?? -1;
          break;
        default:
          return 0;
      }

      if (aVal === bVal) return 0;
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredTalks(result);
  };

  const handleBookClick = async (talk) => {
    if (!user) {
      // Show login prompt dialog instead of alert
      setSelectedTalk(talk);
      setLoginPromptDialog(true);
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/talks/${talk.id}/booking-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookingInfo(response.data);
      setSelectedTalk(talk);
      setBookingDialog(true);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to fetch booking information' });
    }
  };

  const handleRateClick = (talk) => {
    if (!user) {
      setMessage({ type: 'info', text: 'You must register to rate talks. Registration is quick and free!' });
      setTimeout(() => navigate('/register'), 3000);
      return;
    }
    setSelectedTalk(talk);
    setRating(0);
    setRatingDialog(true);
  };

  const truncateText = (text, maxLength) => {
    if (!text) return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const submitRating = async () => {
    if (rating === 0) {
      setMessage({ type: 'error', text: 'Please select a rating' });
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API}/talks/${selectedTalk.id}/rate`,
        { talk_id: selectedTalk.id, rating },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: 'success', text: 'Rating submitted successfully!' });
      setRatingDialog(false);
      fetchTalks(); // Refresh to get updated rating
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to submit rating' });
    }
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <img src="/logo_6270.png" alt="Rotary District 6270" className="h-16 w-auto" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Speakers Bureau</h1>
                <p className="text-sm text-gray-500">Browse Available Talks</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="outline" onClick={() => setAboutOpen(true)} data-testid="about-button">
                About...
              </Button>
              {user ? (
                <>
                  <Link to="/dashboard">
                    <Button variant="outline" data-testid="dashboard-link">Dashboard</Button>
                  </Link>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      localStorage.removeItem('access_token');
                      localStorage.removeItem('user');
                      window.location.href = '/login';
                    }}
                    data-testid="logout-button"
                  >
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="outline" data-testid="login-link">Login</Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-[#005DAA] hover:bg-[#004A8F]" data-testid="register-link">Register</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-16 w-full">
        {message && (
          <Alert className={`mb-6 ${
            message.type === 'error' ? 'bg-red-50 border-red-200' :
            message.type === 'success' ? 'bg-green-50 border-green-200' :
            'bg-blue-50 border-blue-200'
          }`} data-testid="message-alert">
            <AlertDescription className={message.type === 'error' ? 'text-red-800' : message.type === 'success' ? 'text-green-800' : 'text-blue-800'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        <Card className="card-shadow mb-6">
          <CardHeader
            className="cursor-pointer select-none"
            onClick={() => setFiltersOpen(o => !o)}
          >
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Filter Talks</CardTitle>
                <CardDescription>Search and filter available talks</CardDescription>
              </div>
              <span className="text-gray-400 text-lg">{filtersOpen ? '▲' : '▼'}</span>
            </div>
          </CardHeader>
          {filtersOpen && <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="speaker-filter">Speaker</Label>
                <Input
                  id="speaker-filter"
                  placeholder="Search by speaker name"
                  value={filters.speaker}
                  onChange={(e) => setFilters({ ...filters, speaker: e.target.value })}
                  data-testid="speaker-filter-input"
                />
              </div>
              <div>
                <Label htmlFor="topic-filter">Topic</Label>
                <Input
                  id="topic-filter"
                  placeholder="Search by topic"
                  value={filters.topic}
                  onChange={(e) => setFilters({ ...filters, topic: e.target.value })}
                  data-testid="topic-filter-input"
                />
              </div>
              <div>
                <Label htmlFor="club-filter">Club</Label>
                <Input
                  id="club-filter"
                  placeholder="Search by club name"
                  value={filters.club}
                  onChange={(e) => setFilters({ ...filters, club: e.target.value })}
                  data-testid="club-filter-input"
                />
              </div>
              <div>
                <Label>Min. Rating</Label>
                <div className="flex flex-wrap items-center gap-1 mt-1.5" data-testid="min-rating-filter">
                  <button
                    onClick={() => setFilters({ ...filters, minRating: 0 })}
                    className={`text-xs px-2 py-1.5 rounded border transition-colors ${filters.minRating === 0 ? 'bg-[#005DAA] text-white border-[#005DAA]' : 'border-gray-300 text-gray-600 hover:border-gray-400'}`}
                    data-testid="min-rating-any"
                  >
                    Any
                  </button>
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      onClick={() => setFilters({ ...filters, minRating: star })}
                      className={`text-sm px-2 py-1 rounded border transition-colors ${filters.minRating === star ? 'bg-[#F7A81B] text-white border-[#F7A81B]' : 'border-gray-300 text-gray-600 hover:border-gray-400'}`}
                      data-testid={`min-rating-${star}`}
                    >
                      {star}★+
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>}
        </Card>

        {/* Talks Table */}
        <Card className="card-shadow">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Available Talks</CardTitle>
                <CardDescription>{filteredTalks.length} talks found</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#005DAA]"></div>
                <p className="mt-2 text-gray-600">Loading talks...</p>
              </div>
            ) : filteredTalks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No talks found matching your filters.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full table-hover" data-testid="talks-table">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('speaker')}>
                        Speaker {sortBy === 'speaker' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('topic')}>
                        Topic {sortBy === 'topic' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('club')}>
                        Original Club {sortBy === 'club' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('date')}>
                        Date {sortBy === 'date' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('deliveries')}>
                        Deliveries {sortBy === 'deliveries' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('rating')}>
                        Rating {sortBy === 'rating' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredTalks.map((talk, idx) => (
                      <tr key={talk.id} data-testid={`talk-row-${idx}`}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="cursor-help">
                                  {truncateText(talk.speaker_name, 30)}
                                </span>
                              </TooltipTrigger>
                              {talk.speaker_name && talk.speaker_name.length > 30 && (
                                <TooltipContent>
                                  <p>{talk.speaker_name}</p>
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </TooltipProvider>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="cursor-help">
                                  {truncateText(talk.topic_name, 60)}
                                </span>
                              </TooltipTrigger>
                              {talk.topic_name && talk.topic_name.length > 60 && (
                                <TooltipContent className="max-w-md">
                                  <p>{talk.topic_name}</p>
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </TooltipProvider>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {talk.original_club || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {talk.original_date || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {talk.delivery_count || 0}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {talk.average_rating ? (
                            <span className="flex items-center">
                              <span className="text-[#F7A81B] mr-1">★</span>
                              {talk.average_rating.toFixed(1)}
                              <span className="text-xs text-gray-400 ml-1">({talk.review_count})</span>
                            </span>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {user && talk.clubs && talk.clubs.includes(user.club) ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRateClick(talk)}
                              data-testid={`rate-button-${idx}`}
                            >
                              Rate
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => handleBookClick(talk)}
                              className="bg-[#005DAA] hover:bg-[#004A8F]"
                              data-testid={`book-button-${idx}`}
                            >
                              Book / Rate This
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Login Prompt Dialog */}
      <Dialog open={loginPromptDialog} onOpenChange={setLoginPromptDialog}>
        <DialogContent data-testid="login-prompt-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Account Required</DialogTitle>
            <DialogDescription className="text-base pt-2">
              To book or rate this talk, you need to log in or create an account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Why create an account?</strong>
              </p>
              <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
                <li>Access speaker contact information</li>
                <li>View program chair details</li>
                <li>Rate talks you've attended</li>
                <li>Save your preferences</li>
              </ul>
            </div>
            
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate('/register')}
                className="w-full bg-[#005DAA] hover:bg-[#004A8F] text-white"
                data-testid="goto-register-button"
              >
                Create New Account
              </Button>
              
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="w-full border-[#005DAA] text-[#005DAA] hover:bg-[#005DAA] hover:text-white"
                data-testid="goto-login-button"
              >
                Log In
              </Button>
              
              <Button
                onClick={() => setLoginPromptDialog(false)}
                variant="ghost"
                className="w-full text-gray-600 hover:text-gray-900"
                data-testid="continue-anonymous-button"
              >
                Continue as Anonymous User
              </Button>
            </div>
            
            <p className="text-xs text-gray-500 text-center">
              Registration is quick and free!
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Booking Dialog */}
      <Dialog open={bookingDialog} onOpenChange={setBookingDialog}>
        <DialogContent data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle>Booking Information</DialogTitle>
            <DialogDescription>
              {selectedTalk?.speaker_name} - {selectedTalk?.topic_name}
            </DialogDescription>
          </DialogHeader>
          {bookingInfo && (
            <div className="space-y-4">
              {bookingInfo.speaker_email ? (
                <Alert className="bg-blue-50 border-blue-200 alert-animate">
                  <AlertDescription className="text-blue-800">
                    <p className="font-medium mb-2">Contact the speaker directly:</p>
                    <p>Email: <a href={`mailto:${bookingInfo.speaker_email}`} className="text-[#005DAA] hover:underline">{bookingInfo.speaker_email}</a></p>
                    <p className="text-sm mt-2">Please complete the booking with the speaker. Automated booking will be a future feature.</p>
                  </AlertDescription>
                </Alert>
              ) : (
                <div>
                  <Alert className="bg-blue-50 border-blue-200 mb-4 alert-animate">
                    <AlertDescription className="text-blue-800">
                      <p className="font-medium mb-2">Speaker email not available.</p>
                      <p className="text-sm">Contact the program chair of one of the clubs where this talk has been given:</p>
                    </AlertDescription>
                  </Alert>
                  {bookingInfo.clubs && bookingInfo.clubs.length > 0 ? (
                    <div className="space-y-3">
                      {bookingInfo.clubs.map((club, idx) => (
                        <Card key={idx} className="p-3">
                          <h4 className="font-medium text-gray-900">{club.name}</h4>
                          {club.program_chair_name && (
                            <p className="text-sm text-gray-600">Contact: {club.program_chair_name}</p>
                          )}
                          {club.program_chair_email ? (
                            <p className="text-sm">
                              Email: <a href={`mailto:${club.program_chair_email}`} className="text-[#005DAA] hover:underline">{club.program_chair_email}</a>
                            </p>
                          ) : (
                            <p className="text-sm text-gray-500">Ask your president for {club.name}'s contact info.</p>
                          )}
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600">No club contact information available. Please check with your club's president for other clubs' contact information.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rating Dialog */}
      <Dialog open={ratingDialog} onOpenChange={setRatingDialog}>
        <DialogContent data-testid="rating-dialog">
          <DialogHeader>
            <DialogTitle>Rate This Talk</DialogTitle>
            <DialogDescription>
              {selectedTalk?.speaker_name} - {selectedTalk?.topic_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Rating (1-5 stars)</Label>
              <div className="flex space-x-2 mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setRating(star)}
                    className={`text-3xl ${rating >= star ? 'text-[#F7A81B]' : 'text-gray-300'} hover:text-[#F7A81B] transition-colors`}
                    data-testid={`star-${star}`}
                  >
                    ★
                  </button>
                ))}
              </div>
            </div>
            <Alert className="bg-yellow-50 border-yellow-200 alert-animate">
              <AlertDescription className="text-yellow-800 text-sm">
                Once submitted, ratings cannot be edited.
              </AlertDescription>
            </Alert>
            <div className="flex space-x-2">
              <Button onClick={submitRating} className="flex-1 bg-[#005DAA] hover:bg-[#004A8F]" data-testid="submit-rating-button">
                Submit Rating
              </Button>
              <Button onClick={() => setRatingDialog(false)} variant="outline" className="flex-1" data-testid="cancel-rating-button">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* About Modal */}
      <AboutModal open={aboutOpen} onOpenChange={setAboutOpen} />

      <Footer />
    </div>
  );
}

export default BrowseTalks;
